/**
 * @module app.ext.mcg-informed
 */

import { autoinject } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { IApiActionResults } from 'app/shared/lib/api/api-types';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { safe } from 'app/shared/lib/utilities';
import { Toaster } from 'app/shared/lib/toaster';
import { McgInformedDataMethods as mcgMethods } from 'app/ext/mcg-informed/data-methods';
import { EssDialogCustomElement } from 'app/shared/elements/ess-dialog';
import { guid } from 'app/shared/lib/guid';
import { deepClone, mergeObjects } from 'app/shared/lib/utilities';
import * as CM from 'app/cm/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';
import * as Dom from 'app/shared/lib/dom';

@autoinject
export class PlanOfCare extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////

    @bindable
    protected assessment: Mcg.Assessment | null = null;

    @bindable.stringNull
    public caseId: string | null = null;

    protected attachment: CM.CaseAssessmentAttachment | null = null;
    protected assessmentAttachmentId: string | null | undefined = null;
    protected selectedGuideline: Mcg.Guideline | null = null;
    protected guidelines: Mcg.Guideline[] = [];
    protected readonly: boolean = false;
    protected version: string | undefined = '';

    protected problems: Mcg.Problem[] = [];
    protected goals: Mcg.IGoal[] = [];

    protected hasProblemIdentified: boolean = false;
    protected selectedProblem: Mcg.Problem | null = null;
    protected selectedGoals: Mcg.IGoal[] = [];
    protected selectedInterventions: Mcg.IIntervention[] = [];

    protected showPlanOfCareHeader: boolean = false;

    protected carePlanGoals: CM.CarePlanGoal[] = [];
    protected carePlanProblemId: string | null | undefined = null;
    protected checkedTrue: string = 'problems';
    protected id: guid = guid();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly mcgService: McgInformedService,
        private readonly carePlanService: CarePlanService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    public hasChanges(): boolean {
        return true;
    }

    public async submit(): Promise<boolean> {
        return true;
    }

    ////****************** Events ***************************************************////

    protected problemSelectionChanged(problem: Mcg.Problem | null): void {
        if(!problem) return;
        this.selectedProblem = problem;
        this.loadGoals();
    }

    protected goalCheckedChanged(goal: Mcg.IGoal | null, checked: boolean, checkbox: HTMLElement): void {
        if(!goal) return;

        let essCheckbox = checkbox.parentElement?.parentElement;
        goal.sanitizedText = essCheckbox?.querySelector('.SanitizedText')?.innerHTML!;

        if(checked && !this.selectedGoals.includes(goal)) this.selectedGoals.push(goal);
        if(!checked && this.selectedGoals.includes(goal)) this.selectedGoals = this.selectedGoals.filter(g => g !== goal);

        if(!checked) {
            this.selectedInterventions = this.selectedInterventions.filter(i => !goal.interventionsSet.some(gi => gi.uid == i.uid));
        }

        this.events.publish('mcg-goal-checked-changed', this.selectedGoals.length > 0);
    }

    protected interventionCheckedChanged(goal: Mcg.IGoal | null, intervention: Mcg.IIntervention | null, checked: boolean, checkbox: HTMLElement): void {
        if(!goal || !intervention) return;

        if(checked && !this.selectedGoals.includes(goal)) {
            this.selectedGoals.push(goal);

            let goalContainer = Dom.getParentByClassName(checkbox, 'goal-container');
            let goalCheckbox = goalContainer?.querySelector('input');
            if(goalCheckbox) goalCheckbox!.checked = checked;
        }

        if(checked && !this.selectedInterventions.includes(intervention)) this.selectedInterventions.push(intervention);
        if(!checked && this.selectedInterventions.includes(intervention)) this.selectedInterventions = this.selectedInterventions.filter(i => i !== intervention);;

        this.events.publish('mcg-goal-checked-changed', this.selectedGoals.length > 0);
    }

    protected goalExpandedToggle(pivotArrow: HTMLAnchorElement, index: number): void {
        let goal: HTMLElement | null | undefined = pivotArrow.parentElement?.parentElement;
        if(!goal) return;

        let interventionsList: HTMLElement | null = goal.querySelector('.interventions-list');

        if(!interventionsList) return;

        if(pivotArrow.classList.contains('fa-angle-right')) {
            pivotArrow.classList.remove('fa-angle-right');
            pivotArrow.classList.add('fa-angle-down');
            interventionsList.classList.remove('interventions-list-hide');
            goal.classList.add('goal-container-expanded');
        }
        else {
            pivotArrow.classList.remove('fa-angle-down');
            pivotArrow.classList.add('fa-angle-right');
            interventionsList.classList.add('interventions-list-hide');
            goal.classList.remove('goal-container-expanded');
        }
        this.goals[index].$expanded = !this.goals[index].$expanded;
    }

    protected async save(modal: EssDialogCustomElement): Promise<void> {
        let success = true;

        const carePlanProblemId = await this.createProblem();
        if(!carePlanProblemId) throw Error('Error creating problem');

        for(const mcgGoal of this.selectedGoals) {
            if(!this.goalExistsOnCareplan(mcgGoal)) success = success && await this.addGoalToCase(mcgGoal, carePlanProblemId);
            else {
                const carePlanGoalId = this.carePlanGoals.find(cpg => cpg.externalIdentifier === mcgGoal.uid)?.id;
                const interventions: Mcg.IIntervention[] = this.selectedInterventions.filter(i => mcgGoal?.interventionsSet.some(gi => gi.uid === i.uid)).filter(i => !this.interventionExistsOnCareplan(i));
                if(carePlanGoalId && interventions?.length > 0) await this.addInterventionsToCase(mcgGoal, carePlanGoalId, interventions);
            }
        }

        if(success) modal.close();
    }

    public async open(attachmentId: string, modal: EssDialogCustomElement, readonly: boolean = false): Promise<void> {
        this.reset();
        this.assessmentAttachmentId = attachmentId;
        this.readonly = readonly;

        await this.loadAssessment();
        this.showPlanOfCareHeader = true;
        modal.open();
    }

    ////****************** Functions ************************************************////

    public async reload(): Promise<void> {
        if(!this.assessment) return;
        this.reset();
        this.version = mcgMethods.sanitizeVersion(this.assessment.contentVersion);
        await this.loadCurrentCarePlan();
        this.loadProblems();
    }

    public reset(): void {
        this.selectedGuideline = null;
        this.guidelines = [];
        this.readonly = false;
        this.version = '';
        this.problems = [];
        this.hasProblemIdentified = false;
        this.goals = [];
        this.selectedProblem = null;
        this.selectedGoals = [];
        this.selectedInterventions = [];
        this.showPlanOfCareHeader = false;
        this.carePlanGoals = [];
        this.carePlanProblemId = null;
    }

    protected async loadAssessment(): Promise<void> {
        if(!this.assessmentAttachmentId) { return; }

        await this.loadAttachment(this.assessmentAttachmentId!);
        await this.loadCurrentCarePlan();
        this.loadProblems();
    }

    protected loadProblems(): void {
        if(!this.assessment) { return; }

        this.problems = [];
        let problems: Mcg.Problem[] = [];
        let uniqueProblems: Mcg.Problem[] = [];

        this.assessment.sectionsSet?.forEach(section => {
            section.sectionsSet.forEach(subSection => {
                subSection.problemsSet.forEach(problem => {
                    if(problem.identified) this.hasProblemIdentified = true;
                    problems.push(problem);
                });
            });
        });

        problems.forEach(p => {
            if(!uniqueProblems.some(p2 => p2.id === p.id)) uniqueProblems.push(p);
        });

        this.problems.push(...uniqueProblems);

        this.loadGoals();
    }

    protected loadGoals(): void {
        this.goals = [];

        if(!this.selectedProblem && this.problems.length == 1) this.selectedProblem = this.problems[0];
        if(!this.selectedProblem) return;

        this.selectedProblem.goalsSet.forEach(goal => {
            goal.interventionsSet.forEach(gi => {
                this.carePlanGoals.forEach(cpg => {
                    if(cpg.interventions?.some(i => gi.uid === i.externalIdentifier)) this.selectedInterventions.push(gi);
                });
            });

            const exp = { $expanded: false };
            const newGoal = mergeObjects(deepClone(goal), exp);
            this.goals.push(newGoal);
        });
    }

    protected isGoalSelected(goal: Mcg.IGoal): boolean {
        return this.selectedGoals.includes(goal);
    }

    protected isInterventionSelected(intervention: Mcg.IIntervention): boolean {
        return this.selectedInterventions.includes(intervention);
    }

    private async createProblem(): Promise<string | null | undefined> {
        if(!this.selectedProblem) return null;
        if(this.problemExistsOnCareplan(this.selectedProblem)) return this.carePlanProblemId;

        const p: CM.CarePlanProblem = {
            isCustom: true,
            description: this.selectedProblem.text,
            externalSystem: { code: 'CCI' },
            externalIdentifier: this.selectedProblem.uid,
            case: { id: this.attachment?.case?.id }
        };

        let carePlanProblemId = await this.addProblemToCase(p);
        return carePlanProblemId;
    }

    public async getSelectedGoals(): Promise<Mcg.IGoal[]> {
        return this.selectedGoals;
    }

    public async getSelectedInterventions(): Promise<Mcg.IIntervention[]> {
        return this.selectedInterventions;
    }

    public async getSelectedProblem(): Promise<Mcg.Problem | null> {
        return this.selectedProblem;
    }

    protected problemExistsOnCareplan(problem: Mcg.Problem): boolean {
        let exists: boolean = false;

        this.carePlanGoals.forEach(cpg => {
            if(cpg.problem?.externalIdentifier === problem.uid) {
                exists = true;
                this.carePlanProblemId = cpg.problem.id;
            }
        });

        return exists;
    }

    protected goalExistsOnCareplan(goal: Mcg.IGoal): boolean {
        let exists: boolean = false;

        this.carePlanGoals.forEach(cpg => {
            if(cpg.externalIdentifier === goal.uid) {
                exists = true;
            }
        });

        return exists;
    }

    public interventionExistsOnCareplan(intervention: Mcg.IIntervention): boolean {
        let exists: boolean = false;

        this.carePlanGoals.forEach(cpg => {
            if(cpg.interventions?.some(i => i.externalIdentifier === intervention.uid)) {
                exists = true;
            }
        });

        return exists;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadAttachment(attachmentId: string): Promise<void> {
        const includes: string[] = ['externalContent'];

        try {
            this.state.beginLoad();
            const response = await this.mcgService.loadAttachment(attachmentId, includes);
            if(!response.isSuccessful || response.results.length === 0) return;

            const attachment: CM.CaseAssessmentAttachment = response.results[0];

            this.attachment = attachment;

            const externalContent = safe(() => attachment!.externalContent);
            if(!externalContent) { throw new Error('Assessment attachment content was not loaded'); }

            const content = externalContent.content;
            if(!content) { throw new Error('Assessment attachment content was not loaded'); }

            this.assessment = JSON.parse(content);
            if(!this.assessment?.contentVersion) return;
            this.version = mcgMethods.sanitizeVersion(this.assessment.contentVersion);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async addProblemToCase(problem: CM.CarePlanProblem): Promise<string | null> {
        try {
            this.state.beginSave();
            const response = await this.carePlanService.insertCarePlanProblem(problem);

            if(response.isSuccessful) return response.id;
            return null;
        }
        catch(error) {
            this.toaster.standard.saved('cm:carePlanProblem.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async addGoalToCase(mcgGoal: Mcg.IGoal, carePlanProblemId: string | null): Promise<boolean> {
        const goal: CM.CarePlanGoal = {
            isCustom: true,
            description: mcgMethods.sanitizeText(mcgGoal),
            externalSystem: { code: 'CCI' },
            externalIdentifier: mcgGoal.uid,
            externalAssessmentIdentifier: this.assessment?.hsim,
            externalAssessmentVersion: this.assessment?.contentVersion,
            assessmentAttachment: { id: this.attachment?.id },
            case: { id: this.attachment?.case?.id },
            problem: { id: carePlanProblemId }
        };

        let interventionsSaved: boolean = true;

        try {
            this.state.beginSave();
            const response = await this.carePlanService.insertCarePlanGoal(goal);

            if(response.isSuccessful && response.id) {
                const carePlanGoalId: string | null = response.id;
                const interventions: Mcg.IIntervention[] = this.selectedInterventions.filter(i => mcgGoal?.interventionsSet.some(gi => gi.uid === i.uid)).filter(i => !this.interventionExistsOnCareplan(i));
                if(carePlanGoalId && interventions?.length > 0) interventionsSaved = await this.addInterventionsToCase(mcgGoal, carePlanGoalId, interventions);
            }

            if(response.isSuccessful && interventionsSaved) {
                this.toaster.standard.saved('cm:carePlanGoal.$', true);
                return Promise.resolve(true);
            }

            return Promise.resolve(false);
        }
        catch(error) {
            this.toaster.standard.saved('cm:carePlanGoal.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async addInterventionsToCase(mcgGoal: Mcg.IGoal, carePlanGoalId: string, interventions: Mcg.IIntervention[]): Promise<boolean> {
        const insertInterventions: CM.CarePlanIntervention[] = [];

        interventions.forEach(i => {
            const intervention: CM.CarePlanIntervention = {
                isCustom: true,
                description: mcgMethods.sanitizeText(i),
                externalSystem: { code: 'CCI' },
                externalIdentifier: i.uid,
                goal: { id: carePlanGoalId },
                summary: {
                    mediaType: { code: 'mcg.annotation' },
                    taskSummary: i.text
                }
            };

            insertInterventions.push(intervention);
        });

        try {
            this.state.beginSave();

            const promises = new Array<Promise<IApiActionResults>>();
            insertInterventions.forEach(async i => {
                promises.push(this.carePlanService.insertCarePlanIntervention(i));
            });

            const responses = await Promise.all(promises);
            if(responses.every(r => r.isSuccessful)) {
                this.toaster.standard.saved('cm:carePlanIntervention.$', true);
                return Promise.resolve(true);
            }

            return Promise.resolve(false);
        } catch(error) {
            this.toaster.standard.saved('cm:carePlanIntervention.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadCurrentCarePlan(): Promise<boolean> {
        if(!this.caseId && (!this.attachment || !this.attachment.case || !this.attachment.case.id)) return Promise.resolve(false);
        const caseId: string = this.caseId ? this.caseId : this.attachment!.case!.id!;

        try {
            this.state.beginLoad();
            const response = await this.mcgService.loadCaseMcgGoals(caseId);

            if(response.isSuccessful) {
                this.carePlanGoals = response.results;
                return Promise.resolve(true);
            }

            return Promise.resolve(false);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
